import React from "react";
import PropTypes from "prop-types";
import { lineItemsAuthIsValid } from "features/bonafide/helpers";
import HasRolePermission from "components/shared/has-role-permission";
import { OrderProfileContext } from "components/order-profile-new/context";
import { formatDate } from "utils/misc";

const AuthForm = ({
  orderLineItems,
  handleCancel,
  currentStep,
  canOverrideOrderCreation,
  hasPendingTask,
  submitOrder
}) => {
  const { updateOrderLineItemByIdApi } = React.useContext(OrderProfileContext);
  const canCreateAuth = lineItemsAuthIsValid(orderLineItems);
  return (
    <div style={{ overflow: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          padding: 5,
          gridGap: 30,
          height: "100%"
        }}
      >
        <table style={{ margin: 0 }}>
          <thead>
            <tr>
              <td>Item</td>
              <td>HCPCS</td>
              <td>Quantity</td>
              <td>Auth Number</td>
              <td>Auth Start Date</td>
              <td>Auth End Date</td>
            </tr>
          </thead>
          <tbody>
            {orderLineItems.map(item => (
              <tr key={item.line_item_id}>
                <td>{item.item_name}</td>
                <td>{item.hcpc}</td>
                <td>{item.quantity}</td>
                <td>
                  <AuthNumberInput
                    authNumber={item?.authNumber ?? ""}
                    updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                    line_item_id={item.line_item_id}
                  />
                </td>
                <td>
                  <AuthDateInput
                    date={formatDate(item.authStartDate, "YYYY-MM-DD")}
                    field="authStartDate"
                    updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                    line_item_id={item.line_item_id}
                  />
                </td>
                <td>
                  <AuthDateInput
                    date={formatDate(item.authEndDate, "YYYY-MM-DD")}
                    field="authEndDate"
                    updateOrderLineItemByIdApi={updateOrderLineItemByIdApi}
                    line_item_id={item.line_item_id}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div
          style={{
            display: "grid",
            flex: 1,
            gridTemplateRows: "auto auto auto",
            alignItems: "center",
            justifyContent: "center",
            gridGap: 5,
            margin: 5
          }}
        >
          {currentStep === 2 && !hasPendingTask && (
            <button
              className="button"
              type="submit"
              disabled={!canCreateAuth}
              onClick={submitOrder}
            >
              Submit Order For Processing
            </button>
          )}
          {canOverrideOrderCreation && (
            <HasRolePermission
              allowedRoles={[
                "Administrator",
                "OfficeAdministrator",
                "CompanyAdministrator",
                "ServiceCoach",
                "ServiceAdmin",
                "ContractorAdministrator"
              ]}
            >
              <button
                className="button"
                type="submit"
                disabled={!canCreateAuth}
                onClick={submitOrder}
              >
                Send Order For Processing
              </button>
            </HasRolePermission>
          )}
          <span className="button warning" onClick={handleCancel}>
            Cancel
          </span>
        </div>
      </div>
    </div>
  );
};

AuthForm.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  orderLineItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  submitOrder: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  canOverrideOrderCreation: PropTypes.bool,
  hasPendingTask: PropTypes.bool
};

export default AuthForm;

const AuthNumberInput = ({
  authNumber,
  disabled,
  line_item_id,
  updateOrderLineItemByIdApi
}) => {
  const [value, setValue] = React.useState(() => authNumber); // initialize once

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleBlur = async () => {
    if (value !== authNumber) {
      await updateOrderLineItemByIdApi.fetchData({
        line_item_id,
        authNumber: value
      });
    }
  };

  return (
    <input
      className="auth-number-input"
      type="text"
      disabled={disabled}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

AuthNumberInput.propTypes = {
  authNumber: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  line_item_id: PropTypes.string.isRequired,
  updateOrderLineItemByIdApi: PropTypes.func.isRequired
};

const AuthDateInput = ({
  line_item_id,
  disabled,
  date,
  field,
  updateOrderLineItemByIdApi
}) => {
  const [value, setValue] = React.useState(() => date);

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleBlur = async () => {
    if (value !== date) {
      await updateOrderLineItemByIdApi.fetchData({
        line_item_id,
        [field]: value
      });
    }
  };
  return (
    <input
      className="auth-date-input"
      type="date"
      disabled={disabled}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

AuthDateInput.propTypes = {
  date: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.string.isRequired,
  line_item_id: PropTypes.string.isRequired,
  updateOrderLineItemByIdApi: PropTypes.func.isRequired
};
